import { supabaseClient } from "@/lib/supabase";
import type { ActionKeyT } from "@/types/database/actions";
import type { Database } from "@/types/database/database";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";

type ActionWordsRow = Database["public"]["Tables"]["action_words"]["Row"];

export async function selectActionsWords(
	key: ActionKeyT,
	userId: string,
): Promise<string | null> {
	// First try to get from action_words
	const { data: actionWordsData, error: actionWordsError } =
		await supabaseClient
			.from("action_words")
			.select("*")
			.eq("action_key", key)
			.eq("user_id", userId)
			.maybeSingle();

	if (actionWordsError) {
		throw actionWordsError;
	}

	if (actionWordsData?.action_words) {
		return actionWordsData.action_words;
	}

	// Fallback to action_definitions
	const { data: definitionData, error: definitionError } = await supabaseClient
		.from("action_definitions")
		.select("email_words")
		.eq("action_key", key)
		.maybeSingle();

	if (definitionError) {
		throw definitionError;
	}

	return definitionData?.email_words ?? null;
}

export const createActionsQueryKey = (actionKey, userId) => [
	"actions-words",
	actionKey,
	userId,
];

export const selectActionsWordsQueryOptions = (
	key: ActionKeyT,
	userId: string,
) => {
	return queryOptions({
		queryKey: [...createActionsQueryKey(key, userId)],
		queryFn: () => selectActionsWords(key, userId),
	});
};

export function useSelectActionsWordsQuery(key: ActionKeyT, userId: string) {
	return useSuspenseQuery(selectActionsWordsQueryOptions(key, userId));
}
